import React, { useState } from 'react';
import { Button, Typography } from 'antd';
import {
  UserOutlined,
  CarOutlined,
  EditOutlined,
  CameraOutlined,
} from '@ant-design/icons';
import { Customer, Vehicle } from '../../types';
import CustomerInlineForm from '../CustomerInlineForm';
import VehicleInlineForm from '../VehicleInlineForm';
import CustomerCompletenessPanel from '../CustomerCompletenessPanel';
import UnifiedAIChat, { UnifiedAIChatProps } from '../UnifiedAIChat';
import PhotoStrip from '../Shared/PhotoStrip';
import CameraCaptureField from '../Shared/CameraCaptureField';
import { assetLabel } from '../../utils/assetLabel';
import type { CustomerMatch } from '../../utils/customerMatcher';
import type { VehicleMatch } from '../../utils/vehicleMatcher';

const { Text } = Typography;

export interface CustomerVehiclePanelProps {
  selectedCustomer: Customer | null;
  selectedVehicle: Vehicle | null;
  onCustomerChange: (customer: Customer) => void;
  onClearCustomer: () => void;
  onEditCustomer: () => void;
  onVehicleChange: (vehicle: Vehicle) => void;
  onClearVehicle: () => void;
  onEditVehicle: () => void;
  aiChatProps: UnifiedAIChatProps;
  customerCandidates: CustomerMatch[];
  onAcceptCustomerCandidate: (customer: Customer) => void;
  onDismissCustomerCandidates: () => void;
  vehicleCandidates: VehicleMatch[];
  onAcceptVehicleCandidate: (vehicle: Vehicle) => void;
  onDismissVehicleCandidates: () => void;
  isEditing: boolean;
  estimateId: string | undefined;
  draftEstimateId: number | null;
  autoSaveStatus: 'idle' | 'saving' | 'saved';
  onStartDraftForPhotos: () => void;
  currentEstimateId: number | undefined;
}

export const CustomerVehiclePanel: React.FC<CustomerVehiclePanelProps> = ({
  selectedCustomer,
  selectedVehicle,
  onCustomerChange,
  onClearCustomer,
  onEditCustomer,
  onVehicleChange,
  onClearVehicle,
  onEditVehicle,
  aiChatProps,
  customerCandidates,
  onAcceptCustomerCandidate,
  onDismissCustomerCandidates,
  vehicleCandidates,
  onAcceptVehicleCandidate,
  onDismissVehicleCandidates,
  isEditing,
  estimateId,
  draftEstimateId,
  autoSaveStatus,
  onStartDraftForPhotos,
}) => {
  // Bump on upload so the condition-photo strip re-fetches immediately.
  const [conditionReloadKey, setConditionReloadKey] = useState(0);
  return (
    <>
      <UnifiedAIChat {...aiChatProps} />

      {/* Customer section */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <UserOutlined style={{ color: '#3b82f6' }} />
          <Text strong style={{ fontSize: '14px' }}>Customer</Text>
        </div>
        {selectedCustomer ? (
          <div style={{
            padding: '8px 12px', background: '#f0f7ff', borderRadius: 8,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8,
          }}>
            <span data-testid="cv-customer-name">
              <strong>{selectedCustomer.first_name} {selectedCustomer.last_name}</strong>
              {' '}— {selectedCustomer.phone || selectedCustomer.email}
            </span>
            <span>
              <Button type="link" size="small" icon={<EditOutlined />} onClick={onEditCustomer}>Edit</Button>
              <Button type="link" size="small" onClick={onClearCustomer}>Change</Button>
            </span>
          </div>
        ) : null}

        {/*
          WP-02. Once a customer is selected, the panel above shows only a name and
          a phone number — an empty address is invisible here, and the address
          fields on the edit form are buried in a collapsed "Additional Details"
          accordion with nothing signalling that they matter. Nobody opens it, so
          the address never gets entered, and the gap surfaces at invoice time when
          the customer is long gone.

          Measured against the `invoice` rule set rather than `estimate`: writing
          the estimate is exactly the moment the customer is standing there, which
          makes it the cheapest possible time to ask. Advisory tone — it never
          blocks saving an estimate.
        */}
        {selectedCustomer?.id && (
          <CustomerCompletenessPanel
            customerId={selectedCustomer.id}
            vehicleId={selectedVehicle?.id ?? null}
            stage="invoice"
            tone="advisory"
          />
        )}

        {!selectedCustomer && (
          <CustomerInlineForm
            value={selectedCustomer || undefined}
            onChange={onCustomerChange}
            aiCustomerCandidates={customerCandidates}
            onAcceptCandidate={onAcceptCustomerCandidate}
            onDismissCandidates={onDismissCustomerCandidates}
          />
        )}
      </div>


      {/* Vehicle section */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <CarOutlined style={{ color: '#f59e0b' }} />
          <Text strong style={{ fontSize: '14px' }}>Vehicle</Text>
        </div>
        {selectedVehicle ? (
          <>
            <div style={{
              padding: '8px 12px', background: '#fffbeb', borderRadius: 8,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8,
            }}>
              <span>
                <strong>{assetLabel(selectedVehicle)}</strong>
                {' '}—{' '}
                {selectedVehicle.asset_type === 'equipment'
                  ? `${(selectedVehicle.hours ?? 0).toLocaleString()} hrs`
                  : `${selectedVehicle.current_mileage?.toLocaleString()} mi`}
                {selectedVehicle.asset_type !== 'equipment' && selectedVehicle.license_plate
                  ? ` | ${selectedVehicle.license_plate}`
                  : ''}
              </span>
              <span>
                <Button type="link" size="small" icon={<EditOutlined />} onClick={onEditVehicle}>Edit</Button>
                <Button type="link" size="small" onClick={onClearVehicle}>Change</Button>
              </span>
            </div>

            {/* Condition photos */}
            {(isEditing && estimateId) || draftEstimateId ? (
              <div style={{ marginBottom: 12 }}>
                <Text style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 4 }}>
                  Condition Photos
                </Text>
                <PhotoStrip
                  entityType="estimate"
                  entityId={isEditing && estimateId ? Number(estimateId) : draftEstimateId!}
                  subType="condition"
                  editable
                  reloadKey={conditionReloadKey}
                />
                <CameraCaptureField
                  purpose="condition"
                  entityType="estimate"
                  entityId={isEditing && estimateId ? Number(estimateId) : draftEstimateId!}
                  onUploaded={() => setConditionReloadKey((k) => k + 1)}
                />
              </div>
            ) : (
              <div style={{ marginBottom: 12 }}>
                <Text style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 4 }}>
                  Condition Photos
                </Text>
                <Button
                  size="small"
                  icon={<CameraOutlined />}
                  loading={autoSaveStatus === 'saving'}
                  onClick={onStartDraftForPhotos}
                >
                  Start draft to add photos
                </Button>
                <Text style={{ fontSize: 11, color: '#9ca3af', display: 'block', marginTop: 4 }}>
                  Saves a draft so you can attach photos now.
                </Text>
              </div>
            )}
          </>
        ) : (
          <VehicleInlineForm
            customerId={selectedCustomer?.id}
            customerName={selectedCustomer
              ? (selectedCustomer.company_name || `${selectedCustomer.first_name} ${selectedCustomer.last_name}`)
              : undefined}
            value={selectedVehicle || undefined}
            onChange={onVehicleChange}
            aiVehicleCandidates={vehicleCandidates}
            onAcceptVehicleCandidate={onAcceptVehicleCandidate}
            onDismissVehicleCandidates={onDismissVehicleCandidates}
          />
        )}
      </div>

    </>
  );
};
